import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Check as CheckIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const UnsubscribePage: React.FC = () => {
  const { trackingUUID } = useParams<{ trackingUUID: string }>();
  const navigate = useNavigate();
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);

  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(
        `${API_URL}/orders/track/${trackingUUID}/unsubscribe`
      );
      return data;
    },
    onSuccess: () => {
      setIsUnsubscribed(true);
    },
  });

  const handleUnsubscribe = () => {
    unsubscribeMutation.mutate();
  };

  const handleCancel = () => {
    navigate(`/track/${trackingUUID}`);
  };

  if (isUnsubscribed || unsubscribeMutation.isSuccess) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 3 }}>
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              You've been unsubscribed
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You've been unsubscribed from notifications for this order.
              You can still track your order at any time.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You can re-enable notifications at any time from the tracking page's notification preferences section.
            </Typography>
            <Button
              variant="contained"
              component={Link}
              to={`/track/${trackingUUID}`}
              startIcon={<ArrowBackIcon />}
              sx={{ mt: 2 }}
            >
              Back to Tracking Page
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (unsubscribeMutation.isError) {
    const errorMessage = (unsubscribeMutation.error as any).response?.data?.message || (unsubscribeMutation.error as any).message;
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 3 }}>
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h5" gutterBottom color="error">
              Error
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {errorMessage || 'Failed to unsubscribe. Please try again.'}
            </Typography>
            <Button
              variant="outlined"
              component={Link}
              to={`/track/${trackingUUID}`}
              startIcon={<ArrowBackIcon />}
              sx={{ mt: 2 }}
            >
              Back to Tracking Page
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 3 }}>
      <Paper sx={{ maxWidth: 600, width: '100%', p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Unsubscribe from Notifications
        </Typography>
        <Alert severity="warning" sx={{ my: 3 }}>
          <Typography variant="body1">
            Are you sure you want to unsubscribe from notifications for order{' '}
            <strong>{trackingUUID?.substring(0, 8)}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You will no longer receive email or SMS updates about this order's status changes.
            You can still track your order at any time using the tracking link, and you can re-enable notifications from the tracking page if you change your mind.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={unsubscribeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleUnsubscribe}
            disabled={unsubscribeMutation.isPending}
          >
            {unsubscribeMutation.isPending ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Unsubscribing...
              </>
            ) : (
              'Unsubscribe'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UnsubscribePage;
